// src/components/Products/StoreProductCard.tsx
import React from "react";
import {
    Box,
    Button,
    Flex,
    Image,
    Text,
    Badge,
} from "@chakra-ui/react";
import { ShoppingCart, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Product } from "../../types/product";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

interface Props {
    product: Product;
    onAddToCart: (product: Product) => void;
}

const StoreProductCard: React.FC<Props> = ({ product, onAddToCart }) => {
    const navigate = useNavigate();
    const { theme } = useCustomTheme();
    const isDark = theme.name === "dark";
    const cardsBorderRadii = "8px";

    const image =
        (product as any)?.thumbnail ||
        ((product as any)?.images && (product as any).images[0]) ||
        product.imageUrl ||
        "https://picsum.photos/seed/fallback-product/600/500";

    return (
        <Box
            bg={theme.surface}
            border={`1px solid ${theme.border}`}
            borderRadius={cardsBorderRadii}
            overflow="hidden"
            boxShadow={theme.shadowSm}
            _hover={{
                transform: "translateY(-4px)",
                boxShadow: theme.shadowMd,
                borderColor: theme.borderStrong,
            }}
            transition="all 0.25s ease"
        >
            <Box
                h={{ base: "180px", md: "220px" }}
                bg={theme.surfaceAlt}
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={4}
            >
                <Image src={image} alt={product.title} maxH="100%" objectFit="contain" />
            </Box>

            <Box p={4}>
                <Flex justify="space-between" align="start" gap={2} mb={2}>
                    <Text fontWeight="800" color={theme.text} noOfLines={2}>
                        {product.title}
                    </Text>
                    <Badge
                        borderRadius="4px"
                        px={2.5}
                        py={1}
                        bg={isDark ? "rgba(72,187,120,0.14)" : "rgb(227 255 242)"}
                        color={isDark ? "green.300" : "green.700"}
                        textTransform="none"
                    >
                        In stock
                    </Badge>
                </Flex>

                <Text color={theme.textMuted} fontSize="sm" noOfLines={2} minH="40px">
                    {product.description}
                </Text>

                <Text
                    mt={3}
                    fontWeight="900"
                    color={theme.success || "#38A169"}
                    fontSize="lg"
                >
                    MWK{product.price}
                </Text>

                <Flex mt={4} gap={3}>
                    <Button
                        flex={1}
                        borderRadius="4px"
                        bgImage={theme.gradients?.primaryButton}
                        color={theme.textOnPrimary}
                        leftIcon={<ShoppingCart size={16} />}
                        _hover={{ bgImage: theme.gradients?.primaryButtonHover }}
                        onClick={() => onAddToCart(product)}
                    >
                        Add to Cart
                    </Button>

                    <Button
                        flex={1}
                        borderRadius="4px"
                        variant="outline"
                        borderColor={theme.borderStrong}
                        color={theme.text}
                        leftIcon={<Eye size={16} />}
                        _hover={{ bg: theme.surfaceAlt }}
                        onClick={() => navigate(`/product/${product._id}`)}
                    >
                        Details
                    </Button>
                </Flex>
            </Box>
        </Box>
    );
};

export default StoreProductCard;